<template>
  <div class="overflow-x-auto text-align card shadow bg-white dark:bg-gray-800 w-full rounded-md p-3 border dark:border-gray-700 flex flex-col min-h-0 h-full">
    <EasyDataTable
      v-if="headers !== null && items !== null"
      :headers="headers"
      :items="items"
      :loading="loading"
      buttons-pagination
    >
      <template #loading>
        <ModernLoader />
      </template>
      <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
        <slot :name="slotName" v-bind="resolveSlotBindings(slotName, slotProps)" />
      </template>
    </EasyDataTable>

    <EasyDataTable
      v-else-if="agents !== null"
      :headers="agentHeaders"
      :items="agents"
      :loading="loading"
    >
      <template #loading>
        <ModernLoader />
      </template>
      <template #item-agentname="{ agentname, agentid, agenttype, item }">
        <div class="flex items-center">
          <div class="mr-3 h-8 w-8 rounded-lg flex items-center justify-center overflow-hidden" :class="resolveAgentIcon(agentid) !== null && agenttype !== 'HelpAgent' ? 'bg-white' : (agenttype === 'HelpAgent' ? 'bg-yellow-100 dark:bg-yellow-900' : '')" :style="resolveAgentIcon(agentid) === null && agenttype !== 'HelpAgent' ? { backgroundColor: getColor(agentid, agentname) } : {}">
            <img v-if="resolveAgentIcon(agentid) !== null" :src="resolveAgentIcon(agentid)" class="w-full h-full object-cover" alt="" />
            <Icon v-else :icon="agenttype === 'HelpAgent' ? 'tabler:help-circle' : 'mingcute:ai-fill'" :width="18" :class="agenttype === 'HelpAgent' ? 'text-yellow-500' : 'text-white'" />
          </div>
          <div class="flex flex-col">
            <span class="font-medium">{{ agentname }}</span>
            <span v-if="resolveModelLabel(item) !== ''" class="text-indigo-500 text-[10px]">{{ t('aiChatAgentSetup.agentInformation.llmModelLabel', 'LLM Model') }}: {{ resolveModelLabel(item) }}</span>
            <span class="text-gray-400 text-[10px]">{{ agentid }}</span>
          </div>
        </div>
      </template>

      <template #item-agentstatus="{ agentstatus }">
        <div class="flex items-center">
          <div :class="['agentStatus', typeof agentstatus === 'string' && agentstatus.toLowerCase() === 'active' ? 'bg-green-500' : 'inactiveAgent']">
            {{ typeof agentstatus === 'string' && agentstatus.toLowerCase() === 'active' ? t('aiChats.statusActive', 'Active') : t('aiChats.statusDisabled', 'Disabled') }}
          </div>
        </div>
      </template>

      <template #item-agenttype="{ agenttype }">
        <div class="flex items-center">
          <div :class="['agentType', agenttype === 'PublicAgent' ? 'bg-blue-500' : 'bg-indigo-500']">
            {{ t('aiChats.agentType.' + agenttype, String(agenttype).replace(/([A-Z])/g, ' $1').trim()) }}
          </div>
        </div>
      </template>

      <template #item-channels="{ item }">
        <div class="flex items-center justify-center">
          <div v-if="resolveChannelIcons(item).length > 0" class="flex flex-wrap items-center justify-center">
            <span v-for="channel in resolveChannelIcons(item)" :key="channel.id" class="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 last:mr-0" :title="channel.label">
              <Icon :icon="channel.icon" width="12" />
            </span>
          </div>
        </div>
      </template>

      <template #item-options="{ rowNumber }">
        <button @click="emit('openChat', rowNumber)" class="mr-4 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          <Icon icon="tabler:message-2" width="16" />
          <span class="btn-text">{{ resolveOpenButtonLabel(rowNumber) }}</span>
        </button>
        <button v-if="resolveShowVoiceOpenButton(rowNumber) === true" @click="emit('openVoice', rowNumber)" class="mr-4 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          <Icon icon="tabler:microphone" width="16" />
          <span class="btn-text">{{ t('aiChats.openVoiceButton', 'Open Voice') }}</span>
        </button>
        <button @click="emit('edit', rowNumber)" class="mr-4 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          <Icon icon="tabler:edit" width="16" />
          <span class="btn-text">{{ t('aiChats.editButton', 'Edit') }}</span>
        </button>
        <button @click="emit('deploy', rowNumber)" class="mr-4 hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <Icon icon="tabler:rocket" width="16" />
          <span class="btn-text">{{ t('aiChats.deployButton', 'Deploy') }}</span>
        </button>
        <button @click="emit('status', rowNumber)" class="mr-4 rtl:ml-4 hover:text-blue-900 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <Icon icon="tabler:info-circle" width="16" />
          <span class="btn-text">{{ t('aiChats.statusButton', 'Status') }}</span>
        </button>
        <button @click="emit('delete', rowNumber)" class="hover:text-red-900 p-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          <Icon icon="tabler:trash" width="16" />
          <span class="btn-text">{{ t('aiChats.deleteButton', 'Delete') }}</span>
        </button>
      </template>
    </EasyDataTable>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from '@/i18n.js';
import { Icon } from "@iconify/vue/dist/offline";
import ModernLoader from '@/components/ModernLoader.vue';

const props = defineProps({
  headers: {
    type: Array,
    default: null
  },
  items: {
    type: Array,
    default: null
  },
  agents: {
    type: Array,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  styleLogos: {
    type: Object,
    default: () => ({})
  },
  getOpenButtonLabel: {
    type: Function,
    default: null
  },
  getChannelIcons: {
    type: Function,
    default: null
  },
  getShowVoiceOpenButton: {
    type: Function,
    default: null
  }
});

const emit = defineEmits(['openChat', 'openVoice', 'edit', 'deploy', 'status', 'delete']);
const { t } = useI18n();

const agentHeaders = computed(() => {
  return [
    { text: '#', value: 'rowNumber' },
    { text: t('aiChats.table.headerAgentName', 'Agent Name'), value: 'agentname' },
    { text: t('aiChats.table.headerStatus', 'Status'), value: 'agentstatus' },
    { text: t('aiChats.table.headerChannels', 'Channels'), value: 'channels' },
    { text: t('aiChats.table.headerType', 'Type'), value: 'agenttype' },
    { text: t('aiChats.table.headerOptions', 'Options'), value: 'options' }
  ];
});

function resolveAgentIcon(agentId) {
  if (props.styleLogos !== undefined && props.styleLogos !== null && props.styleLogos[agentId] !== undefined && props.styleLogos[agentId] !== null && props.styleLogos[agentId] !== '') {
    return props.styleLogos[agentId];
  }
  return null;
}

function getColor(agentId, agentName) {
  const id = String(agentId !== undefined && agentId !== null ? agentId : agentName);
  let hash = 0;
  for (let i = 0; i < id.length; i = i + 1) {
    hash = ((hash << 5) - hash) + id.charCodeAt(i);
    hash = hash & hash;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 65%)`;
}

function resolveOpenButtonLabel(rowNumber) {
  if (typeof props.getOpenButtonLabel === 'function') {
    const resolved = props.getOpenButtonLabel(rowNumber);
    if (typeof resolved === 'string' && resolved.trim() !== '') {
      return resolved;
    }
  }
  return t('aiChatAgentSetup.buttons.openAgent', 'Open Agent');
}

function resolveChannelIcons(agent) {
  if (typeof props.getChannelIcons === 'function') {
    const resolved = props.getChannelIcons(agent);
    if (Array.isArray(resolved) === true) {
      return resolved;
    }
  }
  return [];
}

function resolveShowVoiceOpenButton(rowNumber) {
  if (typeof props.getShowVoiceOpenButton === 'function') {
    return props.getShowVoiceOpenButton(rowNumber) === true;
  }
  return false;
}

function resolveModelLabel(agent) {
  if (agent !== null && agent !== undefined && typeof agent.modelLabel === 'string' && agent.modelLabel.trim() !== '') {
    return agent.modelLabel.trim();
  }
  if (agent !== null && agent !== undefined && agent.agentdata !== undefined && agent.agentdata !== null && typeof agent.agentdata.selectedLlmModelId === 'string' && agent.agentdata.selectedLlmModelId.trim() !== '') {
    return agent.agentdata.selectedLlmModelId.trim();
  }
  if (agent !== null && agent !== undefined && typeof agent.selectedLlmModelId === 'string' && agent.selectedLlmModelId.trim() !== '') {
    return agent.selectedLlmModelId.trim();
  }
  return '';
}

function resolveSlotBindings(slotName, slotProps) {
  if (typeof slotName === 'string' && slotName.startsWith('item-') === true) {
    if (slotProps !== null && typeof slotProps === 'object' && Array.isArray(slotProps) === false) {
      return {
        item: slotProps,
        ...slotProps
      };
    }
    return { item: slotProps };
  }
  return slotProps;
}
</script>

<style scoped>
.agentStatus {
  color: #ffffff;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
}

.inactiveAgent {
  background-color: #6b7280;
}

.agentType {
  color: #ffffff;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
}

:deep(.action-btn) {
  margin-inline-end: 1rem;
  padding: 0.5rem 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 0.375rem;
  background-color: #e0e7ff;
  color: #4338ca;
  font-size: 0.875rem;
  line-height: 1rem;
  font-weight: 500;
  outline: none;
  transition: background-color 0.2s ease, color 0.2s ease;
}

:deep(.action-btn:last-child) {
  margin-inline-end: 0;
}

:deep(.action-btn:hover) {
  background-color: #c7d2fe;
}

:deep(.action-btn:focus) {
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #6366f1;
}

:deep(.btn-delete) {
  background-color: #e0e7ff;
  color: #4338ca;
}

:deep(.btn-delete:hover) {
  background-color: #c7d2fe;
}

:deep(.btn-edit) {
  background-color: #e0e7ff;
  color: #4338ca;
}

:deep(.btn-text) {
  margin-inline-start: 0.35rem;
}

:deep(.action-btn:disabled),
:deep(.action-btn-disabled) {
  background-color: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 1;
}
</style>
